import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, Globe, Send, Sparkles, Loader2, Circle, Trash2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useEntity, createItem, updateItem } from "../hooks/useEntity";
import api, { apiError } from "../lib/api";
import { formatDateTime, initials } from "../lib/utils-format";

const CH_ICON = { Email: Mail, SMS: MessageSquare, "Website Chat": Globe };

export default function Conversations() {
  const [filter, setFilter] = useState("all");
  const [accountId, setAccountId] = useState("all");
  const [accounts, setAccounts] = useState([]);
  const params = { limit: 100 };
  if (filter === "unread") params.unread = "true";
  if (filter === "ai") params.ai_managed = "true";
  if (filter === "human") params.needs_human = "true";
  if (accountId !== "all") params.account_id = accountId;
  const { items, loading, reload } = useEntity("conversations", params);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/accounts?limit=500&sort_by=company_name&sort_dir=asc")
      .then((r) => setAccounts(r.data.items || []))
      .catch(() => setAccounts([]));
  }, []);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader title="Conversations" subtitle="Unified inbox across email, SMS, and chat" count={items.length} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "unread", "ai", "human"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`conv-filter-${f}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
            {f === "all" ? "All" : f === "ai" ? "AI-Managed" : f === "human" ? "Needs Human" : "Unread"}
          </button>
        ))}
        <div className="ml-auto w-56">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger data-testid="conv-account-filter" className="h-8 text-xs">
              <SelectValue placeholder="Filter by account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="conv-account-all">All accounts</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`conv-account-${a.id}`}>
                  {a.company_name || "Untitled account"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4 rounded-md border border-border bg-card overflow-hidden" style={{ height: "calc(100vh - 240px)" }}>
        <div className="border-r border-border overflow-y-auto slim-scroll">
          {loading ? <div className="p-4 text-sm text-muted-foreground">Loading…</div> :
            items.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">No conversations</div> :
            items.map((c) => {
              const Icon = CH_ICON[c.last_channel || c.channel] || Mail;
              return (
                <button key={c.id} onClick={() => setSelected(c)} data-testid={`conv-item-${c.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-accent/50 transition-colors ${selected?.id === c.id ? "bg-accent" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm truncate flex-1">{c.lead_name}</span>
                    {c.unread && <Circle className="h-2 w-2 fill-primary text-primary" />}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{c.subject}</div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <StatusBadge value={c.conversation_status} />
                    {c.ai_managed && <StatusBadge value="Active" />}
                  </div>
                </button>
              );
            })}
        </div>
        <div className="overflow-hidden flex flex-col">
          {selected ? <Thread conv={selected} onChanged={reload} onDeleted={() => { setSelected(null); reload(); }} /> :
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>}
        </div>
      </div>
    </div>
  );
}

function Thread({ conv, onChanged, onDeleted }) {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get(`/messages?conversation_id=${conv.id}&sort_by=created_at&sort_dir=asc&limit=100`).then((r) => setMessages(r.data.items));
  useEffect(() => { setReply(""); load(); /* eslint-disable-next-line */ }, [conv.id]);

  // A thread can span multiple channels. Determine the channels actually used,
  // and the "active" channel to reply on (latest inbound, else latest message).
  const channelsUsed = [...new Set(messages.map((m) => m.channel).filter(Boolean))];
  const lastInbound = [...messages].reverse().find((m) => m.direction === "inbound");
  const lastMsg = messages[messages.length - 1];
  const replyChannel = lastInbound?.channel || lastMsg?.channel || conv.last_channel || conv.channel || "Email";
  const headerChannels = channelsUsed.length ? channelsUsed.join(" · ") : (conv.last_channel || conv.channel);

  const draft = async () => {
    const last = [...messages].reverse().find((m) => m.direction === "inbound");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/draft-reply", { conversation_id: conv.id, message: last?.body || conv.subject });
      setReply(data.reply);
    } catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await createItem("messages", { conversation_id: conv.id, channel: replyChannel, direction: "outbound", body: reply, sender: "You", status: "Delivered" });
      await updateItem("conversations", conv.id, { unread: false, last_channel: replyChannel, last_message_at: new Date().toISOString() });
      setReply(""); load(); onChanged?.();
      toast.success(`Message sent via ${replyChannel}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/conversations/${conv.id}?hard=true`);
      toast.success("Conversation deleted");
      onDeleted?.();
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  return (
    <>
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">{conv.lead_name}</div>
          <div className="text-xs text-muted-foreground" data-testid="conv-channels">{conv.subject} · {headerChannels}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge value={conv.sentiment} />
          <Button size="sm" variant="outline" onClick={() => updateItem("conversations", conv.id, { conversation_status: "Closed" }).then(onChanged)} data-testid="close-conv-btn">Close</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" data-testid="delete-conv-btn" disabled={deleting}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-conv-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the conversation with {conv.lead_name} and all its messages. This can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-conv-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="delete-conv-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto slim-scroll p-5 space-y-4">
        {messages.map((m) => {
          const MIcon = CH_ICON[m.channel] || Mail;
          return (
            <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm ${m.direction === "outbound" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-70 mb-1">
                  <MIcon className="h-3 w-3" />
                  <span data-testid={`msg-channel-${m.id}`}>{m.channel || "Email"}</span>
                  <span className="opacity-60">· {m.sender}</span>
                </div>
                {m.body}
                <div className="text-[10px] opacity-60 mt-1">{formatDateTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-3">
        <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply via ${replyChannel}…`} rows={2} data-testid="reply-input" />
        <div className="flex justify-between mt-2">
          <Button size="sm" variant="outline" onClick={draft} disabled={aiLoading} data-testid="ai-draft-btn">
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" />AI Draft</>}
          </Button>
          <Button size="sm" onClick={send} disabled={sending} data-testid="send-message-btn"><Send className="h-4 w-4 mr-1.5" />Send</Button>
        </div>
      </div>
    </>
  );
}
